import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { createTenantApi, tenantsApi, type TenantApi } from '../api/endpoints';
import type { Role, Tenant } from '../api/types';
import { Loading } from '../components/ui';
import { useCurrentUser } from './AuthProvider';

interface TenantScopeValue {
  tenant: Tenant;
  api: TenantApi;
  role: Role;
  /** Prefix for links inside this client space, e.g. `/t/<id>`. */
  base: string;
}

const TenantScopeContext = createContext<TenantScopeValue | null>(null);

/**
 * Everything under `/t/:tenantId` runs inside one client space. Regular users
 * can only open their own; the platform administrator can open any of them.
 */
export function TenantScope({ children }: { children: ReactNode }) {
  const { tenantId = '' } = useParams();
  const user = useCurrentUser();
  const isPlatformAdmin = user.role === 'platform_admin';
  const allowed = isPlatformAdmin || user.tenantId === tenantId;

  const tenant = useQuery({ queryKey: ['tenant', tenantId], queryFn: () => tenantsApi.get(tenantId), enabled: allowed });
  const api = useMemo(() => createTenantApi(isPlatformAdmin ? tenantId : null), [isPlatformAdmin, tenantId]);

  if (!allowed) return <Navigate to={user.tenantId ? `/t/${user.tenantId}/plans` : '/admin/tenants'} replace />;
  if (tenant.isError) return <Navigate to={isPlatformAdmin ? '/admin/tenants' : '/'} replace />;
  if (!tenant.data) return <Loading />;

  return (
    <TenantScopeContext.Provider value={{ tenant: tenant.data, api, role: user.role, base: `/t/${tenantId}` }}>
      {children}
    </TenantScopeContext.Provider>
  );
}

export function useTenant(): TenantScopeValue {
  const value = useContext(TenantScopeContext);
  if (!value) throw new Error('useTenant doit être utilisé dans TenantScope');
  return value;
}
